import { ChangeDetectorRef, Pipe, PipeTransform } from '@angular/core';
import { DataService } from '../../core/providers/data/data.service';
import {
  CurrencyCode,
  GetActiveChannelQuery,
  PriceRange,
  SearchResultPrice,
  SinglePrice,
} from './../../graphql/generated/generated-types';
import { map, of, take } from 'rxjs';
import { GET_ACTIVE_CHANNEL } from './get-active-channel.graphql';
import { replaceISKtoKr } from '../utils/replace-isk-to-kr';
import { CustomLocaleReplacement } from '../utils/custom-currencies';

/**
 * Cache the resolved promise so that the activeChannel only needs
 * to be fetched once on the first invocation of the pipe.
 */
let channelDataPromise: Promise<any>;

/**
 * A pipe which formats a price (always given as an integer by Venure) according
 * to the currencyCode of the current Channel.
 */

@Pipe({
  name: 'formatSearchResultPriceTemplate',
  standalone: true,
})
export class FormatSearchResultPriceTemplatePipe implements PipeTransform {
  constructor(private dataService: DataService) {}

  transform(value: SearchResultPrice, qtyInCart?: number): any {
    if ('min' in value) {
      return of(this.formatPriceRange(value as PriceRange, qtyInCart));
    } else {
      return of(this.formatSinglePrice(value as SinglePrice, qtyInCart));
    }
    /* UNCOMMENT WHEN MORE THAN ONE CURRENCY IS REQUIRED
    return this.dataService.query<GetActiveChannelQuery>(GET_ACTIVE_CHANNEL).pipe(
      take(1),
      map(data => {
        const channel = data.activeChannel;
        if ('min' in value) {
          return this.formatPriceRange(value as PriceRange, qtyInCart, channel);
        } else {
          return this.formatSinglePrice(value as SinglePrice, qtyInCart, channel);
        }
      })
    );
    */
  }

  private formatPriceRange(
    priceRange: PriceRange,
    qtyInCart?: number,
    channel?: any
  ): string {
    const formatter = Intl.NumberFormat(CustomLocaleReplacement.IS, {
      style: 'currency',
      currency: channel ? channel.currencyCode : CurrencyCode.ISK,
    });
    if (qtyInCart) {
      let formattedPrice =
        '' + '' + formatter.format((priceRange.min * qtyInCart) / 100);
      if (channel && channel.currencyCode == CurrencyCode.ISK) {
        formattedPrice = replaceISKtoKr(formattedPrice);
      } else {
        formattedPrice = replaceISKtoKr(formattedPrice);
      }
      return formattedPrice;
    } else {
      let formattedPrice = '' + '' + formatter.format(priceRange.min / 100);
      if (channel && channel.currencyCode == CurrencyCode.ISK) {
        formattedPrice = replaceISKtoKr(formattedPrice);
      } else {
        formattedPrice = replaceISKtoKr(formattedPrice);
      }
      return formattedPrice;
    }
  }

  private formatSinglePrice(
    singlePrice: SinglePrice,
    qtyInCart?: number,
    channel?: any
  ): string {
    const formatter = Intl.NumberFormat(CustomLocaleReplacement.IS, {
      style: 'currency',
      currency: channel ? channel.currencyCode : CurrencyCode.ISK,
    });

    if (qtyInCart) {
      let formattedPrice = formatter.format(
        (singlePrice.value * qtyInCart) / 100
      );
      if (channel && channel.currencyCode == CurrencyCode.ISK) {
        formattedPrice = replaceISKtoKr(formattedPrice);
      } else {
        formattedPrice = replaceISKtoKr(formattedPrice);
      }
      return formattedPrice;
    } else {
      let formattedPrice = formatter.format(singlePrice.value / 100);
      if (channel && channel.currencyCode == CurrencyCode.ISK) {
        formattedPrice = replaceISKtoKr(formattedPrice);
      } else {
        formattedPrice = replaceISKtoKr(formattedPrice);
      }
      return formattedPrice;
    }
  }
}
